import React from 'react';
import { Tag, Tooltip, Avatar, Badge } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined, DatabaseOutlined, FacebookFilled, InstagramOutlined, WhatsAppOutlined, UserOutlined } from "@ant-design/icons";
import RowActions from '../../Shared/RowActions/RowActions';
import { format } from 'date-fns';

export const TABLE_CLASS = 'credentials-table';
export const ROW_CLASS_HIGHLIGHT = `${TABLE_CLASS}__row--highlight`;

export const formatDate = (dateString) => {
    return dateString ? format(new Date(dateString), 'yyyy-MM-dd HH:mm') : '';
};

export const getRowClassName = (record, highlightedRowIds) => {
    const classes = [];
    if (highlightedRowIds?.has?.(record?.id)) classes.push(ROW_CLASS_HIGHLIGHT);
    return classes.join(' ');
};

export const getTokenExpiryColor = (expiresAt, isPermanent) => {
    if (isPermanent) return 'success';
    if (!expiresAt) return 'default';

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = (expiryDate - now) / (1000 * 60 * 60 * 24);

    if (daysUntilExpiry < 0) return 'error'; // Expired
    if (daysUntilExpiry < 7) return 'warning'; // Expiring soon
    return 'processing'; // Valid
};

export const getTokenExpiryText = (expiresAt, isPermanent, t) => {
    if (isPermanent) return t('permanent_token');
    if (!expiresAt) return t('unknown');

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return t('expired');
    if (daysUntilExpiry === 0) return t('expires_today');
    if (daysUntilExpiry === 1) return t('expires_tomorrow');
    if (daysUntilExpiry < 7) return t('expires_in_days', { days: daysUntilExpiry });
    return t('valid');
};

export const getChannelIcon = (channelType, isBadge = false) => {
    const style = isBadge ? { fontSize: 12, color: '#fff' } : { fontSize: 20 };
    const bgStyle = isBadge ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: '2px solid #fff'
    } : {};

    let icon = <UserOutlined style={{ ...style, color: '#888' }} />;

    if (channelType === 'facebook_page') {
        return isBadge ? (
            <div style={{ ...bgStyle, backgroundColor: '#1877F2' }}>
                <FacebookFilled style={style} />
            </div>
        ) : <FacebookFilled style={{ ...style, color: '#1877F2' }} />;
    }

    if (channelType === 'instagram') {
        return isBadge ? (
            <div style={{ ...bgStyle, backgroundColor: '#E4405F' }}>
                <InstagramOutlined style={style} />
            </div>
        ) : <InstagramOutlined style={{ ...style, color: '#E4405F' }} />;
    }

    if (channelType === 'whatsapp') {
        return isBadge ? (
            <div style={{ ...bgStyle, backgroundColor: '#25D366' }}>
                <WhatsAppOutlined style={style} />
            </div>
        ) : <WhatsAppOutlined style={{ ...style, color: '#25D366' }} />;
    }

    return icon;
};

export const getColumns = (t, screens, onAction, deleteLoading) => [
    {
        title: '',
        key: 'visual',
        width: 80,
        render: (_, record) => (
            <Badge
                count={getChannelIcon(record.channel_type, true)}
                offset={[-5, 5]}
            >
                <Avatar
                    src={record.picture_url}
                    icon={<UserOutlined />}
                    shape="circle"
                    size="large"
                />
            </Badge>
        ),
    },
    {
        title: t('id'),
        dataIndex: "id",
        key: "id",
        sorter: true,
        width: 90,
    },
    // channel_type column removed
    {
        title: t('name'),
        dataIndex: "name",
        key: "name",
        sorter: true,
    },
    {
        title: t('user_info'),
        key: "user_info",
        render: (_, record) => (
            <div>
                {record.first_name && record.last_name && (
                    <div>{`${record.first_name} ${record.last_name}`}</div>
                )}
                {record.email && (
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.email}</div>
                )}
            </div>
        ),
    },
    {
        title: t('token_status'),
        key: "token_status",
        render: (_, record) => {
            const color = getTokenExpiryColor(record.token_expires_at, record.is_permanent_token);
            const text = getTokenExpiryText(record.token_expires_at, record.is_permanent_token, t);
            return (
                <Tooltip title={record.token_expires_at ? formatDate(record.token_expires_at) : ''}>
                    <Tag color={color}>{text}</Tag>
                </Tooltip>
            );
        }
    },
    {
        title: t('token_expires_at'),
        dataIndex: "token_expires_at",
        key: "token_expires_at",
        sorter: true,
        render: (date) => formatDate(date),
    },
    {
        title: t('created_at'),
        dataIndex: "created_at",
        key: "created_at",
        sorter: true,
        render: (date) => formatDate(date),
    },
    {
        title: t('actions'),
        key: "actions",
        render: (text, record) => (
            <RowActions
                screens={screens}
                primary={{
                    label: t('view_details'),
                    icon: <EyeOutlined />,
                    onClick: () => onAction('view', record),
                    tooltip: t('view_details')
                }}
                items={[
                    {
                        key: 'resources',
                        label: t('view_resources'),
                        icon: <DatabaseOutlined />,
                        onClick: () => onAction('resources', record)
                    },
                    {
                        key: 'edit',
                        label: t('edit_credential'),
                        icon: <EditOutlined />,
                        onClick: () => onAction('edit', record)
                    },
                    {
                        key: 'delete',
                        label: deleteLoading?.[record.id] ? t('deleting') : t('delete_credential'),
                        icon: <DeleteOutlined />,
                        danger: true,
                        disabled: deleteLoading?.[record.id],
                        confirm: {
                            title: t('delete_credential'),
                            content: t('are_you_sure_you_want_to_delete_this_credential'),
                            okText: t('yes'),
                            cancelText: t('no')
                        },
                        onClick: () => onAction('delete', record)
                    }
                ]}
            />
        ),
    }
];
